import React, { useContext, useMemo, useState } from 'react'
import PropTypes from 'prop-types'
import { PASSWORD_STATUSES, UPLOAD_STAGES } from '../constants'
import {
  FileItemDeleted,
  FileItemError,
  FileItemPasswordRequired,
  FileItemProcessing,
  FileItemSuccess,
  FileItemUploading,
} from './FileItemStatus'
import { FileUploaderContext } from './FileUploader'
import { useUploader } from '../hooks'

const progressLabels = {
  uploading: 'uploading...',
  processing: 'processing...',
}

export function FileItem({ id, file: fileState, error: errorState }) {
  const { submitPassword } = useContext(FileUploaderContext)
  const file = fileState.file

  const [currentPassword, setCurrentPassword] = useState('')
  const status = useMemo(() => {
    const progress = Math.round(fileState.progress)
    const uploading = fileState.stage === UPLOAD_STAGES.UPLOADING && progress < 100
    const processing =
      fileState.stage === UPLOAD_STAGES.PROCESSING || (fileState.stage === UPLOAD_STAGES.UPLOADING && progress === 100)
    let progressLabel = ''
    if (uploading || processing) {
      if (uploading) {
        progressLabel = progressLabels.uploading
      } else {
        progressLabel = progressLabels.processing
      }
    }
    const requiresAttention = fileState.stage === UPLOAD_STAGES.AWAITING_PASSWORD
    const passwordSubmitStatus = (() => {
      const passwordStatus = fileState?.encryption?.passwordStatus
      if (
        !requiresAttention ||
        !passwordStatus ||
        [PASSWORD_STATUSES.VERIFYING, PASSWORD_STATUSES.REQUIRED].includes(passwordStatus)
      )
        return ''

      if (fileState.encryption.passwordStatus === PASSWORD_STATUSES.VERIFIED) return 'success'

      return 'error'
    })()
    const passwordVerifying = requiresAttention && fileState?.encryption?.passwordStatus === PASSWORD_STATUSES.VERIFYING
    if (passwordVerifying) {
      progressLabel = progressLabels.processing
    }
    const completed = fileState.stage === UPLOAD_STAGES.COMPLETED

    return {
      progress: progress,
      progressLabel: progressLabel,
      uploading: uploading,
      processing: processing,
      requiresAttention: requiresAttention,
      passwordSubmitStatus: passwordSubmitStatus,
      passwordVerifying,
      completed: completed,
      failed: fileState.stage === UPLOAD_STAGES.FAILED,
      error: fileState.stage === UPLOAD_STAGES.FAILED ? errorState.message : null,
    }
  }, [fileState])

  return (
    <>
      {status.uploading && (
        <FileItemUploading
          id={id}
          fileName={file.name}
          progress={status.progress}
          progressLabel={status.progressLabel}
        />
      )}
      {(status.processing || status.passwordVerifying) && (
        <FileItemProcessing fileName={file.name} id={id} progressLabel={status.progressLabel} />
      )}
      {status.failed && <FileItemError fileName={file.name} id={id} error={status.error} />}
      {status.completed && (
        <FileItemSuccess
          fileName={file.name}
          id={id}
          fileSize={file.size}
          fileUrl={fileState.metadata?.urls?.pdf || fileState.metadata?.urls?.original}
        />
      )}
      {status.requiresAttention && !status.passwordVerifying && (
        <FileItemPasswordRequired
          fileName={file.name}
          id={id}
          status={status.passwordSubmitStatus}
          password={currentPassword}
          onSubmit={(value) => {
            setCurrentPassword(value)
            submitPassword(id, value)
          }}
        />
      )}
      {status.deleted && <FileItemDeleted fileName={file.name} />}
    </>
  )
}

FileItem.propTypes = {
  id: PropTypes.string.isRequired,
  file: PropTypes.any,
  error: PropTypes.any,
}
